/** Time lost and gained against the reference, metre by metre.
 *
 * The one chart a comparison is really for. The speed trace shows two laps;
 * this shows the difference between them as the thing a driver feels: a line
 * that climbs where time is going and falls where it is coming back, read
 * against the corners along the bottom. Above zero is time lost and wears
 * the warning colour, below is time gained and wears the good one — the same
 * two colours the sector chips use, so a chip and a stretch of this chart
 * agree without a key.
 *
 * The axis is metres along the reference lap, after the chosen lap has been
 * lined up with it by where it was on the road. A lap that could not be
 * lined up keeps its own distance, and the card says so, because a delta
 * drawn between two laps on two axes drifts a little further from the truth
 * at every corner.
 */

import type { ReactNode } from 'react';

import { Card, Kicker, Spinner } from '@/components/ui';
import { DASH, delta, deltaTone } from '@/lib/format';
import { CornerAxis, CornerLines } from '@/components/sessions/CornerAxis';
import {
  DELTA_BOX,
  cornerTicks,
  deltaDomain,
  deltaPaths,
  finalDeltaMs,
  zeroY,
} from '@/components/sessions/compare';
import type { CornerMark, DeltaSeries } from '@/components/sessions/compare';
import type { Reference } from '@/components/sessions/references';

export interface DeltaCardProps {
  lapNumber: number | null;
  reference: Reference | null;
  /** Null while nothing is loaded, or when the chosen lap is the reference. */
  delta: DeltaSeries | null;
  isReference: boolean;
  aligned: boolean;
  corners: readonly CornerMark[];
  sectorDistances: readonly number[];
  loading: boolean;
  /** The gap at the line, from the two lap times (`gapAtLineMs`); without
   *  it the card falls back to the trace's last value. */
  atLineMs?: number | null;
}

const TONE: Record<ReturnType<typeof deltaTone>, string> = {
  warn: 'var(--color-warn)',
  ok: 'var(--color-ok)',
  level: 'var(--color-neutral-300)',
  none: 'var(--color-neutral-600)',
};

export function DeltaCard({
  lapNumber,
  reference,
  delta: trace,
  isReference,
  aligned,
  corners,
  sectorDistances,
  loading,
  atLineMs,
}: DeltaCardProps) {
  const domain = deltaDomain(trace);
  const paths = trace && domain ? deltaPaths(trace, domain) : null;
  const zero = domain ? zeroY(domain) : DELTA_BOX.height / 2;
  const ticks = domain ? cornerTicks(corners, domain.maxDistance) : [];
  const atLine = atLineMs !== undefined ? atLineMs : finalDeltaMs(trace);

  return (
    <Card elevation="none" label="Time delta" style={{ padding: 12, gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
        <Kicker>
          time delta · lap {lapNumber ?? DASH}
          {reference ? ` vs ${reference.label}` : ''}
        </Kicker>
        <span className="tabular-nums" style={{ fontSize: 11, color: TONE[deltaTone(atLine)] }}>
          {atLine === null ? '' : `${delta(atLine)} s at the line`}
        </span>
      </div>

      {isReference ? (
        <Empty>
          This is the reference lap. Choose another lap in the table, or another driver to compare
          with, to see where time went.
        </Empty>
      ) : !domain || !paths ? (
        <Empty>
          {loading ? (
            <Spinner label="comparing the laps" />
          ) : (
            'Nothing to compare yet: both laps need a distance axis and a clock.'
          )}
        </Empty>
      ) : (
        <>
          <svg
            viewBox={`0 0 ${DELTA_BOX.width} ${DELTA_BOX.height}`}
            preserveAspectRatio="none"
            role="img"
            aria-label={`Time delta against the reference for lap ${lapNumber ?? ''}: ${
              atLine === null ? 'unknown' : `${delta(atLine)} seconds at the line`
            }`}
            style={{ width: '100%', height: DELTA_BOX.height, display: 'block' }}
          >
            <defs>
              <clipPath id="delta-lost">
                <rect x={0} y={0} width={DELTA_BOX.width} height={zero} />
              </clipPath>
              <clipPath id="delta-gained">
                <rect x={0} y={zero} width={DELTA_BOX.width} height={DELTA_BOX.height - zero} />
              </clipPath>
            </defs>
            <CornerLines ticks={ticks} height={DELTA_BOX.height} />
            {sectorDistances.map((metres) => (
              <line
                key={metres}
                x1={(metres / domain.maxDistance) * DELTA_BOX.width}
                y1={0}
                x2={(metres / domain.maxDistance) * DELTA_BOX.width}
                y2={DELTA_BOX.height}
                stroke="var(--color-divider)"
                strokeDasharray="2 4"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            <path d={paths.area} fill="var(--color-warn)" fillOpacity={0.22} clipPath="url(#delta-lost)" />
            <path d={paths.area} fill="var(--color-ok)" fillOpacity={0.22} clipPath="url(#delta-gained)" />
            <line
              x1={0}
              y1={zero}
              x2={DELTA_BOX.width}
              y2={zero}
              stroke="var(--color-neutral-500)"
              vectorEffect="non-scaling-stroke"
            />
            <polyline
              points={paths.line}
              fill="none"
              stroke="var(--color-text)"
              strokeWidth={1.2}
              vectorEffect="non-scaling-stroke"
            />
          </svg>
          <div
            className="tabular-nums"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 10,
              color: 'var(--color-neutral-600)',
            }}
          >
            <span>+{domain.limit.toFixed(domain.limit < 1 ? 1 : 0)} s lost at the top</span>
            <span>−{domain.limit.toFixed(domain.limit < 1 ? 1 : 0)} s gained at the bottom</span>
          </div>
          <CornerAxis ticks={ticks} width={DELTA_BOX.width} />
          {!aligned ? (
            <p style={{ margin: 0, fontSize: 11.5, color: 'var(--color-warn)', textWrap: 'pretty' }}>
              This lap could not be lined up with the reference by where it was on the road, so each
              lap keeps its own distance and the delta drifts a little at every corner.
            </p>
          ) : null}
        </>
      )}
    </Card>
  );
}

function Empty({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: DELTA_BOX.height,
        fontSize: 12.5,
        color: 'var(--color-neutral-600)',
        textAlign: 'center',
        textWrap: 'pretty',
      }}
    >
      {children}
    </div>
  );
}

export default DeltaCard;
